/** @prettier */

import {useState, useEffect, useContext, memo} from 'react';
import {Grid, Box, Stack, IconButton} from '@mui/material';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';

//MTH component import
import {GraphButton, ControlButton} from './SiteSubContentsIconButton';
//API import
import {
  getStatesSitesFloors,
  getCompositionsGroups,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {GraphFormType, GraphTargetType, mysite} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import * as mySiteLib from './mySiteLibrary';
import {ContextApplyFilterTab} from './MySiteArea';
import {iconBlack, iconWhite, limit} from '../../define';
import {numberWithPeriod, roundOff} from '../../functions/convertString';

//フロア一覧の幅一覧
//変更した場合、ホバーした際のボタンのレイアウトを確認すること
const gridXsAirFloor = 2.5;
const gridXsAirUnit = 4;
const gridXsAirKwh = 4.5;
const gridXsAirArrow = 1;

/**
 * マイサイト操作画面（フロア）
 * @param props props
 * @returns JSX
 */
const ViewFloor = memo(
  (props: {
    siteProfile: apiIF.resParameters.siteInformations;
    setGraphParam: (
      graphTarget: GraphTargetType,
      graphTargetID?: string,
      graphType?: GraphFormType,
      graphTitle?: string
    ) => void;
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    /** フィルタ、タブ状態をセットする関数 */
    const {applyFilter, applyTab} = useContext(ContextApplyFilterTab);
    const trans = useTranslation().t; //翻訳関数

    //prettier-ignore
    const [viewFloorData, setViewFloorData] = useState<apiIF.resParameters.floorStates[]>([]); //フロアデータ
    //prettier-ignore
    const [viewFloorPwData, setViewFloorPwData] = useState<mysite.floorPwData[]>([]); //フロア消費電力量データ
    const [floorList, setFloorList] = useState<string[]>([]); //フロア表示用リスト この配列の順番に画面描画される
    //prettier-ignore
    const [floorAuthority, setFloorAuthority] = useState<{[floor: string]: 'manager' | 'viewer' | 'none'}>({}); //フロアごとの権限
    const [selectedFloor, setSelectedFloor] = useState(''); //選択状態のフロア
    const [hoveringFloor, setHoveringFloor] = useState(''); //ホバー状態のフロア
    //prettier-ignore
    const [hoveringIcon, setHoveringIcon] = useState<'' | 'graph' | 'control'>(''); //ホバー状態のアイコン

    /** サイトID */
    const siteId = props.siteProfile['site-id'];
    /** APIで問い合わせる */
    const afterTime =
      props.siteProfile['timezone'] === undefined
        ? undefined
        : mySiteLib.timezone2aftertime(props.siteProfile['timezone']);

    //最新フロア状態値取得用（消費電力量以外）
    useEffect(() => {
      /**
       * フロア階層を降順にソート
       * @param floorData フロア受信データ
       * @returns フロア階層
       */
      const sortFloorList = (floorData: apiIF.resParameters.floorStates[]) => {
        const floorList = floorData
          //フロア階数を抽出
          .map(floorOneData => floorOneData['floor'])
          //フロア階層を降順にソート
          .sort((a, b) => {
            const aFloorNum = Number(a);
            const bFloorNum = Number(b);
            return bFloorNum - aFloorNum;
          });

        return floorList;
      };

      /**
       * フロアデータ取得
       */
      const reqStatesSitesFloors = () => {
        //最新データ取得
        const query: apiIF.reqParameters.getStatesSitesFloors = {
          fields:
            'register-number,operating-number,malfunctioning-number,error-code',
        };
        getStatesSitesFloors(siteId, query)
          .then(response => {
            //最新の稼働状況データ（配列の先頭が最新データ）
            setViewFloorData(response['floor-states'][0]['states']);
            //フロアリストをソートする
            setFloorList(sortFloorList(response['floor-states'][0]['states']));
          })
          .catch(err => {
            setFloorList([]);
          });
      };

      //ページ遷移時初回読み込み
      reqStatesSitesFloors();
      //定期実行
      const intervalId = setInterval(() => {
        reqStatesSitesFloors();
      }, 60000);
      return function () {
        clearInterval(intervalId);
      };
    }, [siteId]);

    //消費電力取得用
    //１日分のデータ処理はAPIの応答に時間がかかるので、消費電力量だけ分離する
    useEffect(() => {
      /**
       * フロア別種別消費電力量
       * @param recvFloorData フロア情報
       * @return フロア別消費電力量
       */
      const calcPwFloorData = (
        recvFloorData: apiIF.resParameters.floorStates[]
      ) => {
        /**
         * API戻り値の消費電力量WをKW変換(小数点以下四捨五入)
         */
        const convNumKw = mySiteLib.convNumKw;

        return recvFloorData.map(floorData => {
          //受信データに消費電力がない場合は、0とする
          if (isUndefined(floorData['power-consumption'])) {
            return {'floor': floorData['floor'], 'power-consumption': '0'};
          }

          //フロア別消費電力量の、冷房・暖房・その他を合計する
          const cooling = convNumKw(
            floorData['power-consumption']['cooling-mode']
          );
          const heating = convNumKw(
            floorData['power-consumption']['heating-mode']
          );
          const other = convNumKw(floorData['power-consumption']['other-mode']);
          const pwData = String(cooling + heating + other);
          return {'floor': floorData['floor'], 'power-consumption': pwData};
        });
      };

      /**
       * フロアデータ取得
       */
      const reqStatesSitesFloorsPw = () => {
        //最新データ取得 (消費電力量取得)
        const query: apiIF.reqParameters.getStatesSitesFloors = {
          'after-time': afterTime,
          'fields': 'power-consumption',
          'every-minute': 1440, //60min × 24h  １日分のデータ取得
        };

        getStatesSitesFloors(siteId, query)
          .then(response => {
            //最新の稼働状況データ (配列の先頭が最新データ)
            setViewFloorPwData(
              calcPwFloorData(response['floor-states'][0]['states'])
            );
          })
          .catch(err => {});
      };

      //ページ遷移時初回読み込み
      reqStatesSitesFloorsPw();
      //定期実行
      const intervalId = setInterval(() => {
        reqStatesSitesFloorsPw();
      }, 60000);
      return function () {
        clearInterval(intervalId);
      };
    }, [siteId, afterTime]);

    //フロアの権限取得用
    useEffect(() => {
      getCompositionsGroups(siteId, {
        'fields': 'floor,group-authority',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      }).then(response => {
        //フロアごとの権限を取得
        const authority = response['group-informations'].reduce(
          (result, groupInfo) => {
            if (
              groupInfo.floor === undefined ||
              groupInfo['group-authority'] === undefined
            ) {
              return result;
            }

            if (groupInfo.floor in result) {
              //フロアに登録されているグループの最上位権限を採用
              switch (result[groupInfo.floor]) {
                case 'manager':
                  break;
                case 'viewer':
                  if (groupInfo['group-authority'] !== 'none') {
                    result[groupInfo.floor] = groupInfo['group-authority'];
                  }
                  break;
                case 'none':
                  result[groupInfo.floor] = groupInfo['group-authority'];
                  break;
              }
            } else {
              result[groupInfo.floor] = groupInfo['group-authority'];
            }
            return result;
          },
          {} as {[floor: string]: 'manager' | 'viewer' | 'none'}
        );
        setFloorAuthority(authority);
      });
    }, [siteId]);

    /**
     * フロア名変換テーブル
     * @param floor フロア
     * @returns フロア名
     */
    const convertFloorName = (floor: string) => {
      switch (floor) {
        case '14':
          return trans('ViewFloor.body.floor.floor14');
        case '13':
          return trans('ViewFloor.body.floor.floor13');
        case '12':
          return trans('ViewFloor.body.floor.floor12');
        case '11':
          return trans('ViewFloor.body.floor.floor11');
        case '10':
          return trans('ViewFloor.body.floor.floor10');
        case '9':
          return trans('ViewFloor.body.floor.floor9');
        case '8':
          return trans('ViewFloor.body.floor.floor8');
        case '7':
          return trans('ViewFloor.body.floor.floor7');
        case '6':
          return trans('ViewFloor.body.floor.floor6');
        case '5':
          return trans('ViewFloor.body.floor.floor5');
        case '4':
          return trans('ViewFloor.body.floor.floor4');
        case '3':
          return trans('ViewFloor.body.floor.floor3');
        case '2':
          return trans('ViewFloor.body.floor.floor2');
        case '1':
          return trans('ViewFloor.body.floor.floor1');
        case '0':
          return trans('ViewFloor.body.floor.floor0');
        case '-1':
          return trans('ViewFloor.body.floor.basement1');
        case '-2':
          return trans('ViewFloor.body.floor.basement2');
        case '-3':
          return trans('ViewFloor.body.floor.basement3');
        case '-4':
          return trans('ViewFloor.body.floor.basement4');
        default:
          return '';
      }
    };

    /**
     * フロア数を元に検索し、フロアデータを返す
     * @param floor フロア階層
     * @return フロア消費電力量データ
     */
    const findFloorData = (floor: apiIF.resParameters.floorStates['floor']) => {
      return viewFloorData.find(elem => elem['floor'] === floor);
    };

    /**
     * フロア数を元に検索し、フロア消費電力量データを返す
     * @param floor フロア階層
     * @return フロア消費電力量データ
     */
    const findFloorPwData = (floor: mysite.floorPwData['floor']) => {
      return viewFloorPwData.find(elem => elem['floor'] === floor);
    };

    /**
     * フロアが選択状態か判定
     * @param floor フロア（-4から14）
     */
    const selectHandle = (floor: string) => {
      if (floor === selectedFloor) {
        setSelectedFloor('');
      } else {
        setSelectedFloor(floor);
      }
    };

    return (
      <>
        {/** ヘッダ */}
        <Grid
          container
          alignItems="center"
          className="subContents__content__header">
          <Grid
            item
            xs={gridXsAirFloor}
            className="subContents__content__header__category">
            {trans('ViewFloor.header.floor')}
          </Grid>
          <Grid
            item
            xs={gridXsAirUnit}
            className="subContents__content__header__status">
            {trans('ViewFloor.header.status')}
          </Grid>
          <Grid
            item
            xs={gridXsAirKwh + gridXsAirArrow}
            className="subContents__content__header__power">
            {trans('ViewFloor.header.consumption')}
          </Grid>
        </Grid>
        {/** フロア一覧 */}
        <Box className="subContents__content__body">
          <Stack>
            {/* floorListの順番に表示する ソートされていればその順番になる */}
            {floorList.map((floor, index) => {
              /** フロアデータ */
              const floorData = findFloorData(floor);
              /** フロア消費電力量データ */
              const floorPowerData = findFloorPwData(floor);

              if (
                isUndefined(floorData) ||
                isUndefined(floorPowerData) ||
                floorData['register-number'] === '0'
              ) {
                return <Box key={`viewFloorNoData${index}`}></Box>;
              }

              /** 地上階と地下階との間に区切り線を引くための変数（最上位階を除く） */
              const isValidSeparateLine = floorData['floor'] === '-1';
              /** 選択されているかどうか */
              const isSelected = floorData['floor'] === selectedFloor;
              /** ホバーされているかどうか */
              const isHover = floorData['floor'] === hoveringFloor;

              return (
                <Box
                  key={`viewFloorData${floorData['floor']}`}
                  className={
                    index === 0
                      ? ''
                      : isValidSeparateLine
                      ? 'subContents__content__body--floorB1'
                      : 'subContents__content__body--basic'
                  }
                  onClick={() => selectHandle(floorData['floor'])}
                  onMouseEnter={() => setHoveringFloor(floorData['floor'])}
                  onMouseLeave={() => setHoveringFloor('')}>
                  <Grid
                    container
                    alignItems="center"
                    className={`subContents__content__body__data ${
                      isSelected
                        ? 'subContents__content__body__data--selected'
                        : ''
                    }`}>
                    {/** 階層 */}
                    <Grid
                      item
                      xs={gridXsAirFloor}
                      title={convertFloorName(floorData['floor'])}
                      className="subContents__content__body__data__category">
                      {convertFloorName(floorData['floor'])}
                    </Grid>
                    {/** ユニットアイコン、運転台数/登録台数、異常表示 */}
                    <Grid
                      item
                      xs={gridXsAirUnit}
                      container
                      columnSpacing={2}
                      alignItems="center">
                      {/** ユニットアイコン */}
                      <Grid item xs="auto">
                        <Box className="subContents__content__body__data__status__unitIcon__border">
                          <img
                            //ホバー状態and選択されていない場合のみ、白画像
                            src={iconBlack.AC_ON.src}
                            alt={`${trans(
                              `ViewFloor.body.image.alternativeText.${iconBlack.AC_ON.alt}`
                            )}`}
                            className="subContents__content__body__data__status__unitIcon__border__image"
                          />
                        </Box>
                      </Grid>
                      {/** 運転台数/登録台数 */}
                      <Grid item xs="auto">
                        {floorData['operating-number'] !== undefined &&
                          floorData['register-number'] !== undefined && (
                            <>
                              <Box
                                component="span"
                                className="subContents__content__body__data__status__operatingNumber">
                                {floorData['operating-number'].padStart(3, '0')}
                              </Box>
                              /{floorData['register-number'].padStart(3, '0')}
                            </>
                          )}
                      </Grid>
                      {/** 異常表示 */}
                      {/** 異常時のみ表示する */}
                      {floorData['malfunctioning-number'] !== '0' && (
                        <Grid item xs="auto">
                          <Stack alignItems="center" spacing={0.5}>
                            <img
                              src={iconWhite.ERROR.src}
                              alt={`${trans(
                                `ViewFloor.body.image.alternativeText.${iconWhite.ERROR.alt}`
                              )}`}
                              className="subContents__content__body__data__status__errorIcon__icon"
                            />
                            <Box
                              component="span"
                              className="subContents__content__body__data__status__errorIcon__text">
                              {floorData['malfunctioning-number']}
                            </Box>
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                    {/** 消費電力量、グラフ、機器操作 */}
                    <Grid
                      item
                      xs={gridXsAirKwh}
                      container
                      justifyContent="flex-end"
                      className="subContents__content__body__data__powerGraphOpe">
                      {/** 消費電力量 */}
                      <Grid
                        item
                        xs="auto"
                        className={`subContents__content__body__data__powerGraphOpe__power ${
                          isSelected
                            ? 'subContents__content__body__data--selected__powerGraphOpe__power'
                            : ''
                        }`}>
                        {numberWithPeriod(
                          roundOff(floorPowerData['power-consumption'])
                        )}
                      </Grid>
                      <Grid
                        item
                        xs="auto"
                        className={`subContents__content__body__data__powerGraphOpe__KWh ${
                          isSelected
                            ? 'subContents__content__body__data--selected__powerGraphOpe__KWh'
                            : ''
                        }`}>
                        {trans('ViewFloor.body.unit')}
                      </Grid>
                      {/** グラフ、機器操作 */}
                      <Grid
                        item
                        xs="auto"
                        className="subContents__content__body__data__powerGraphOpe__graphOpeIcon">
                        <GraphButton
                          id={`floor${floorData['floor']}BarChartBtn`}
                          isHover={isHover && hoveringIcon === 'graph'}
                          setHoveringIcon={setHoveringIcon}
                          isIconDisabled={floorData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットのみが含まれたフロアなので無効化
                          onClick={() => {
                            props.setGraphParam(
                              'floors',
                              floorData['floor'],
                              'bar',
                              `Floor${convertFloorName(floorData['floor'])}`
                            );
                          }}
                        />
                        {floorData['floor'] in floorAuthority &&
                          floorAuthority[floorData['floor']] === 'manager' && (
                            <ControlButton
                              isHover={isHover && hoveringIcon === 'control'}
                              setHoveringIcon={setHoveringIcon}
                              isIconDisabled={floorData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットのみが含まれたフロアなので無効化
                              onClick={() => {
                                props.setOperationParam(
                                  'floors',
                                  floorData['floor'],
                                  siteId
                                );
                              }}
                            />
                          )}
                      </Grid>
                    </Grid>
                    {/** 矢印 */}
                    <Grid item xs={gridXsAirArrow}>
                      <IconButton
                        onClick={() => {
                          applyFilter({
                            floor: floorData['floor'],
                            groupID: '',
                            otherUnitType: '',
                          });
                          applyTab('groups');
                        }}>
                        <img
                          src={
                            isHover && hoveringIcon === ''
                              ? iconWhite.CHEVRON_RIGHT.src //フロアをホバー中 かつ グラフ・機器操作アイコンをホバー中
                              : iconBlack.CHEVRON_RIGHT.src
                          }
                          alt={`${trans(
                            'ViewFloor.body.image.alternativeText.arrowRight'
                          )}`}
                          className="subContents__content__body__data__arrow"
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </>
    );
  }
);

export default ViewFloor;
